import CustomTooltip from '../../../../shared/CustomTooltip';
import BlockIcon from '@mui/icons-material/Block';
import DoneIcon from '@mui/icons-material/Done';
import {
    Avatar,
    Box,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { openReportAPIRefSliceAction } from '../../../../../app/Slices/ReportAPIRefSlice';
import { useGetProfileCompanyInfoQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import { CustomChip } from '../../../../../layout/styledcomponents/CustomChip';
import GenericTable, {
    tableBodyCellsStyles,
} from '../../../../shared/GenericTable';

export default function IndividualGuestCompanyUsers(params: any) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const apiRef = useGridApiRef();
    const dispatch = useDispatch();

    const location = useLocation();

    const { data: getCompanyUsers } = useGetProfileCompanyInfoQuery(
        `/reports/guests/${location?.state?.params?.id}/users`,
    );

    const theme = useTheme();

    useEffect(() => {
        const csv = apiRef?.current.exportDataAsCsv;
        const print = apiRef?.current.exportDataAsPrint;
        dispatch(
            openReportAPIRefSliceAction({
                exportInfo: {
                    csvExport: csv,
                    printExport: print,
                },
            }),
        );
    }, [apiRef]);

    const columns: MRT_ColumnDef<any>[] = [
        {
            id: 'user',
            accessorFn: (row) =>
                `${row.firstName} ${row.lastName} ${row.email}`,
            header: 'Guest',
            mantineTableBodyCellProps: {
                style: {
                    ...tableBodyCellsStyles,
                    paddingTop: 0,
                    paddingBottom: 0,
                },
            },

            Cell: ({ row }) => {
                const firstName = row.original?.firstName ?? '';
                const lastName = row.original?.lastName ?? '';
                const email = row.original?.email ?? '';

                return (
                    <List>
                        <ListItem sx={{ padding: '0!important' }}>
                            {!isMobile && (
                                <ListItemAvatar>
                                    <Avatar
                                        sx={{
                                            padding: '0!important',
                                            bgcolor: theme.palette.faded.main,
                                        }}
                                    >
                                        {firstName.charAt(0)}
                                        {lastName.charAt(0)}
                                    </Avatar>
                                </ListItemAvatar>
                            )}
                            <ListItemText
                                primary={
                                    <Typography
                                        sx={{
                                            padding: '0!important',
                                        }}
                                    >
                                        {`${firstName} ${lastName}`.trim() ||
                                            '-'}
                                    </Typography>
                                }
                                secondary={
                                    <Typography
                                        sx={{
                                            padding: '0!important',

                                            fontSize: '12px',
                                        }}
                                    >
                                        {email || ''}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                );
            },
        },
        {
            id: 'status',
            accessorFn: (row) => (row.isActive ? 'Enabled' : 'Disabled'),
            header: 'Status',
            size: 130,
            filterVariant: 'select',
            filterFn: (row, _columnId, filterValue) => {
                if (!filterValue) return true;
                const status = row.getValue<string>('status');
                return status === filterValue;
            },

            mantineFilterSelectProps: {
                data: [
                    { label: 'Enabled', value: 'Enabled' },
                    { label: 'Disabled', value: 'Disabled' },
                ],
                placeholder: 'Filter by Status',
                clearable: true,
            },
            Cell: ({ cell }) => {
                const isMobile = useMediaQuery('(max-width:600px)');
                const isIpad = useMediaQuery('(max-width:1024px)');
                const value = cell.getValue<string>() === 'Enabled';

                if (isMobile || isIpad) {
                    return value ? (
                        <CustomTooltip title='Enabled'>
                            <DoneIcon
                                sx={{ color: theme.palette.textAccent.main }}
                            />
                        </CustomTooltip>
                    ) : (
                        <CustomTooltip title='Disabled'>
                            <BlockIcon />
                        </CustomTooltip>
                    );
                }

                return (
                    <CustomChip
                        label={
                            <Typography
                                color={value ? 'green' : 'red'}
                                sx={{
                                    fontSize: '12px',
                                }}
                            >
                                {value ? 'Enabled' : 'Disabled'}
                            </Typography>
                        }
                        color={value ? 'green' : 'red'}
                        variant='outlined'
                    />
                );
            },
        },
        {
            accessorKey: 'lastLoginAt',
            header: 'Last Login',
            size: 120,
            mantineTableBodyCellProps: {
                style: {
                    ...tableBodyCellsStyles,
                    paddingTop: 0,
                    paddingBottom: 0,
                },
            },

            Cell: ({ cell }) => {
                const value = cell.getValue();

                return (
                    <List sx={{ padding: '0!important' }}>
                        <ListItem sx={{ padding: '0!important' }}>
                            <ListItemText
                                primary={
                                    <Typography
                                        sx={{
                                            padding: '0!important',
                                        }}
                                    >
                                        {value
                                            ? moment(value).format('DD/MM/YYYY')
                                            : '-'}
                                    </Typography>
                                }
                                secondary={
                                    <Typography
                                        sx={{
                                            padding: '0!important',

                                            fontSize: '12px',
                                        }}
                                    >
                                        {value
                                            ? moment(value).format('h:mm a')
                                            : ''}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                );
            },
        },

        {
            accessorKey: 'role',
            header: 'Access level',
            filterVariant: 'select',
            mantineFilterSelectProps: {
                data: [
                    { label: 'Company Admin', value: 'guest_admin' },
                    { label: 'Team Admin', value: 'guest_department_admin' },
                    { label: 'Guest User', value: 'guest_user' },
                ],
                placeholder: 'Filter by Status',
                clearable: true,
            },
            size: 120,
            Cell: ({ cell }) => {
                const value = cell.getValue();

                return value === 'guest_admin'
                    ? 'Company Admin'
                    : value === 'guest_department_admin'
                      ? 'Team Admin'
                      : 'Guest User';
            },
        },
        {
            accessorKey: 'bookings',
            header: 'No. Reservations',
            filterFn: 'betweenInclusive',
            size: 100,
        },
    ];

    const processedData = useMemo(() => {
        if (!getCompanyUsers) return [];
        return getCompanyUsers;
    }, [getCompanyUsers]);

    return (
        <Box
            sx={{
                height: 'auto',
                width: isMobile ? '90vw' : '100%',
                paddingTop: '50px',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    overflowX: 'auto',
                }}
            >
                <GenericTable
                    columns={columns}
                    data={processedData}
                    tableName='Guest-Company-Users'
                    loading={!getCompanyUsers}
                />
            </Box>
        </Box>
    );
}
